import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Building2, Globe, Calendar, Tag, MessageSquare, ExternalLink } from 'lucide-react';
import { getLeadSubmissions, updateLeadSubmission, getLandingPages, LeadSubmission } from '../services/api';
import GlassButton from './ui/GlassButton';
import GlassCard from './ui/GlassCard';

const STATUS_COLOURS: Record<string, string> = {
  new: '#2177FF',
  contacted: '#f5c84c',
  qualified: '#36d399',
  disqualified: '#f87171',
  converted: '#1EACA6',
};

export default function LandingPageLeads() {
  const { landingPageId } = useParams<{ landingPageId: string }>();
  const navigate = useNavigate();

  const [leads, setLeads] = useState<LeadSubmission[]>([]);
  const [pageTitle, setPageTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<LeadSubmission | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    if (!landingPageId) return;
    setLoading(true);
    Promise.all([
      getLeadSubmissions(undefined, landingPageId),
      getLandingPages(),
    ]).then(([subs, pages]) => {
      setLeads(subs);
      const page = pages.find(p => p.landing_page_id === landingPageId);
      setPageTitle(page?.title || 'Landing Page');
    }).catch(console.error).finally(() => setLoading(false));
  }, [landingPageId]);

  const filteredLeads = filterStatus ? leads.filter(l => l.status === filterStatus) : leads;

  const handleStatusChange = async (lead: LeadSubmission, status: string) => {
    setUpdatingId(lead.submission_id);
    try {
      const updated = await updateLeadSubmission(lead.submission_id, { status });
      setLeads(prev => prev.map(l => l.submission_id === lead.submission_id ? { ...l, status: updated.status } : l));
      if (selected?.submission_id === lead.submission_id) setSelected({ ...selected, status: updated.status });
    } catch (e) {
      console.error(e);
    } finally {
      setUpdatingId(null);
    }
  };

  const fmt = (iso: string) => new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' }}>
        <GlassButton variant="ghost" size="sm" icon={<ArrowLeft size={16} />} onClick={() => navigate('/landing-pages')}>
          Back
        </GlassButton>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: 'var(--text)' }}>
            Leads — {pageTitle}
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            {leads.length} submission{leads.length !== 1 ? 's' : ''}
          </p>
        </div>
        {/* Filter */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Filter:</span>
          {['', 'new', 'contacted', 'qualified', 'converted', 'disqualified'].map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              style={{
                padding: '4px 12px', borderRadius: '9999px', border: '1px solid var(--stroke)',
                background: filterStatus === s ? 'var(--primary-500)' : 'var(--panel)',
                color: filterStatus === s ? '#fff' : 'var(--text)',
                fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer',
              }}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '48px' }}>Loading…</p>
      ) : filteredLeads.length === 0 ? (
        <GlassCard style={{ textAlign: 'center', padding: '56px' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>
            {filterStatus ? `No ${filterStatus} leads` : 'No leads yet. Share your published page to start capturing leads.'}
          </p>
        </GlassCard>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: '20px', alignItems: 'start' }}>
          {/* Lead table */}
          <GlassCard style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--divider)', background: 'var(--panel-strong)' }}>
                  {['Name', 'Email', 'Phone', 'Status', 'Submitted', ''].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map(lead => (
                  <tr
                    key={lead.submission_id}
                    onClick={() => setSelected(selected?.submission_id === lead.submission_id ? null : lead)}
                    style={{
                      borderBottom: '1px solid var(--divider)',
                      cursor: 'pointer',
                      background: selected?.submission_id === lead.submission_id ? 'var(--panel-active)' : 'transparent',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (selected?.submission_id !== lead.submission_id) (e.currentTarget as HTMLTableRowElement).style.background = 'var(--panel-hover)'; }}
                    onMouseLeave={e => { if (selected?.submission_id !== lead.submission_id) (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--text)' }}>{lead.name || '—'}</td>
                    <td style={{ padding: '10px 16px', color: 'var(--text-secondary)' }}>{lead.email}</td>
                    <td style={{ padding: '10px 16px', color: 'var(--text-secondary)' }}>{lead.phone || '—'}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600,
                        background: `${STATUS_COLOURS[lead.status] || '#64748b'}22`,
                        color: STATUS_COLOURS[lead.status] || '#64748b',
                      }}>
                        {lead.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmt(lead.created_at)}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <select
                        value={lead.status}
                        onClick={e => e.stopPropagation()}
                        onChange={e => handleStatusChange(lead, e.target.value)}
                        disabled={updatingId === lead.submission_id}
                        style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--stroke)', background: 'var(--panel)', color: 'var(--text)', fontSize: '0.78rem', cursor: 'pointer' }}
                      >
                        {['new', 'contacted', 'qualified', 'converted', 'disqualified'].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GlassCard>

          {/* Detail panel */}
          {selected && (
            <GlassCard style={{ padding: '24px', fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--text)' }}>{selected.name || 'Unknown'}</h3>
                  <span style={{
                    display: 'inline-block', marginTop: '6px', padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600,
                    background: `${STATUS_COLOURS[selected.status] || '#64748b'}22`,
                    color: STATUS_COLOURS[selected.status] || '#64748b',
                  }}>
                    {selected.status}
                  </span>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '1.2rem', lineHeight: 1 }}>✕</button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selected.email && <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}><Mail size={15} color="var(--text-secondary)" /><a href={`mailto:${selected.email}`} style={{ color: 'var(--primary-500)' }}>{selected.email}</a></div>}
                {selected.phone && <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}><Phone size={15} color="var(--text-secondary)" /><a href={`tel:${selected.phone}`} style={{ color: 'var(--text)' }}>{selected.phone}</a></div>}
                {selected.company && <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}><Building2 size={15} color="var(--text-secondary)" /><span style={{ color: 'var(--text)' }}>{selected.company}</span></div>}
                {selected.source_url && <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}><Globe size={15} color="var(--text-secondary)" /><a href={selected.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', wordBreak: 'break-all' }}>{selected.source_url}<ExternalLink size={11} style={{ marginLeft: '4px' }} /></a></div>}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}><Calendar size={15} color="var(--text-secondary)" /><span style={{ color: 'var(--text-secondary)' }}>{fmt(selected.created_at)}</span></div>
                {selected.source_widget && <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}><Tag size={15} color="var(--text-secondary)" /><span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>via {selected.source_widget}</span></div>}
              </div>

              {/* Message / custom fields */}
              {selected.custom_fields && Object.keys(selected.custom_fields).length > 0 && (
                <div style={{ marginTop: '20px', padding: '14px', background: 'var(--panel-strong)', borderRadius: '8px', border: '1px solid var(--divider)' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                    <MessageSquare size={13} /> Custom fields
                  </div>
                  {Object.entries(selected.custom_fields).map(([k, v]) => (
                    <div key={k} style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: 600, color: 'var(--text)', textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}: </span>
                      <span style={{ color: 'var(--text-secondary)' }}>{String(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Quiz results */}
              {selected.quiz_score != null && (
                <div style={{ marginTop: '16px', padding: '14px', background: 'var(--panel-strong)', borderRadius: '8px', border: '1px solid var(--divider)' }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.78rem', marginBottom: '8px' }}>Quiz results</div>
                  <div style={{ color: 'var(--text)' }}>Score: <strong>{selected.quiz_score}</strong></div>
                  {selected.quiz_result_level && <div style={{ color: 'var(--text)' }}>Level: <strong>{selected.quiz_result_level}</strong></div>}
                </div>
              )}

              {/* Status update */}
              <div style={{ marginTop: '20px' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Update status</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['new', 'contacted', 'qualified', 'converted', 'disqualified'].map(s => (
                    <button
                      key={s}
                      disabled={selected.status === s || updatingId === selected.submission_id}
                      onClick={() => handleStatusChange(selected, s)}
                      style={{
                        padding: '5px 12px', borderRadius: '9999px', fontSize: '0.78rem', fontWeight: 600,
                        border: `1px solid ${STATUS_COLOURS[s] || '#64748b'}`,
                        background: selected.status === s ? STATUS_COLOURS[s] : 'transparent',
                        color: selected.status === s ? '#fff' : (STATUS_COLOURS[s] || '#64748b'),
                        cursor: selected.status === s ? 'default' : 'pointer',
                        opacity: updatingId === selected.submission_id ? 0.5 : 1,
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </GlassCard>
          )}
        </div>
      )}
    </div>
  );
}
